// Where the merchant page loaded by the phone stands, read again every 250 ms
// until the phone extracts it (doc/ajout-de-produit.md). Sent as source text
// with each listyRenderPage request and run on the merchant's page, in the
// app's isolated JS world: it must stay self-contained (no import, no closure).
// Kept here rather than in the apps so that a new anti-bot page or currency
// ships with a web deploy, not a store release.
//
// Returns { state, resources }:
// - "product": the page already carries its product data (JSON-LD, a price in a
//   meta tag), extract it;
// - "challenge": no product, and a waiting, challenge or blocking page (Imperva,
//   Akamai, Cloudflare, DataDome, PerimeterX, an IP block), with no title or
//   next to no text: never sent, its JS often reloads the real page, and the
//   server takes over otherwise;
// - "loading": neither yet, or only a price on screen (a promo banner shows one
//   before the product arrives: Galeries Lafayette), the phone waits for a
//   quiet network;
// - resources: how many resources the page has loaded, stable 0.5 s = quiet.
//   WebKit keeps 250 entries at most (resource timing buffer): past that the
//   count stops moving and a page still busy reads as quiet. No worse than the
//   former fixed 1 s wait; a finer signal would need state across reads.
//
// `currencies`: the ISO codes Listy offers (Priceable::CURRENCIES).
export function pageReadiness(currencies) {
  const text = document.body ? document.body.innerText || '' : ''
  const resources = performance.getEntriesByType('resource').length

  // Product first: a real product page often loads an anti-bot's script all
  // the time (Cloudflare at Galeries Lafayette, DataDome at La Redoute).
  const productData = Array.from(document.querySelectorAll('script[type="application/ld+json"]')).some((script) =>
    /"@type"\s*:\s*\[?\s*"(Product|ProductGroup)"/.test(script.textContent)
  )
  const priceMeta = Boolean(
    document.querySelector(
      'meta[property="product:price:amount"], meta[property="og:price:amount"], ' +
        'meta[property="og:type"][content="product" i], [itemprop="price"], [itemprop="offers"]'
    )
  )
  // Any currency symbol, or a code or local abbreviation written against the amount.
  const codes = currencies.concat(['FCFA', 'CFA', 'DH', 'DA', 'DT', 'Rs']).join('|')
  const currency = `(\\p{Sc}|\\b(?:${codes})\\b)`
  const priceShown = new RegExp(`\\d[\\d\\s.,]*\\s?${currency}|${currency}\\s?\\d`, 'u').test(text.slice(0, 20000))
  if (productData || priceMeta) return { state: 'product', resources }
  if (priceShown) return { state: 'loading', resources }

  // An anti-bot's marks only count on a nearly empty page, and the HTML is
  // only serialized for one (up to 3 MB, read every 250 ms).
  const bare = text.trim().length < 2000
  const html = bare && document.documentElement ? document.documentElement.innerHTML : ''
  const challenge =
    !document.title.trim() ||
    /^(Access Denied|Attention Required|Just a moment|Un instant)/i.test(document.title.trim()) ||
    /(adresse IP|IP address)[^.]{0,80}(bloqu|block)/i.test(text) ||
    text.trim().length < 300 ||
    (bare &&
      (/Pardon Our Interruption|_Incapsula_Resource/.test(html) ||
        /bm-verify|sec-if-cpt-container|sec-cpt-if/.test(html) ||
        /challenge-platform|challenge-container|cf-chl-/.test(html) ||
        /captcha-delivery\.com|px-captcha/.test(html) ||
        Boolean(document.querySelector('meta[http-equiv="refresh" i]'))))
  if (challenge) return { state: 'challenge', resources }

  return { state: 'loading', resources }
}
